import cx from 'classnames';
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState, useRef, useCallback } from 'react';

import { Store } from '../../state/store.interface';
import IconButton from '../../common/components/buttons/IconButton';

import './Sample.scss';
import styles from './Sample.module.scss';
import { Percentile } from './interfaces';
import * as sampleSlice from './sampleSlice';
import { getAreaPercentIncrease } from './helpers';
import { roundWeight } from '../../config/commons';
import SampleD3, { SampleD3Props } from './SampleD3';
import { SampleFullScreen } from './SampleFullScreen';
import PreviousNextButtons from './PreviousNextButtons';
import { createListPercentiles, createListPercentilesOld, groupLabels } from './math-percentile';

let sampleChart: SampleD3 | null;

export default function Sample (props: { showOptions?: boolean; containerClassName?: string }) {
  const { containerClassName = styles.container, showOptions = true } = props;

  const dispatch = useDispatch();
  const [t] = useTranslation();

  const [image, setImage] = useState(new window.Image());
  const [loading, setLoading] = useState(true);

  const { allAnalysis, analysis: analysisOriginal, currentIndex, theme } = useSelector((state: Store) => state.detail);
  const { percentileList } = useSelector((state: Store) => state.sample);

  const currentAnalysis = (allAnalysis && allAnalysis.length > 0) ? allAnalysis[currentIndex] : analysisOriginal;
  const analysisType = currentAnalysis?.type || '';
  const analysisPhaseType = currentAnalysis.phaseType;
  const larvaeModifiedList = currentAnalysis.inferenceData.larvaeModifiedList;

  const refChart = useRef<HTMLDivElement>(null);

  const averageWeight = currentAnalysis.resultData.averageWeight;

  const updatePercentiles = useCallback(() => {
    if (!currentAnalysis._id || image.height === 0) {
      return;
    }

    const { pointSizeRatio } = currentAnalysis;
    const { larvaeNumber } = currentAnalysis.resultData;
    const { weightGroups } = currentAnalysis.inferenceData;

    const labels = roundWeight({ value: currentAnalysis.resultData.labels, showUnit: false, average: averageWeight });
    const labelsSort: string[] = groupLabels(labels as number[]);

    const areaPercentIncrease = getAreaPercentIncrease({ analysisType }) * larvaeNumber;
    const percentiles: Percentile[] = pointSizeRatio === undefined || pointSizeRatio === null ?
      createListPercentilesOld({ labelsSort, areaPercentIncrease, weightGroups }) :
      createListPercentiles({ labelsSort, pointSizeRatio, weightGroups, imageSize: image.height });

    dispatch(sampleSlice.setPercentileList(percentiles));
  }, [currentAnalysis._id, averageWeight, analysisType, image.height]);

  useEffect(() => {
    setLoading(true);

    if (!currentAnalysis?.imageData?.reduced?.url) {
      return;
    }

    const img = new window.Image();
    img.src = currentAnalysis.imageData.reduced.url;
    img.addEventListener('load', () => {
      setImage(img);
      setLoading(false);
    });
  }, [currentAnalysis?.imageData?.reduced?.url]);

  useEffect(() => {
    updatePercentiles();
  }, [updatePercentiles]);
  
  useEffect(() => {
    if (!currentAnalysis?.imageData?.reduced?.url || !percentileList?.length || !refChart.current || image.height === 0) {
      return;
    }

    if (sampleChart) {
      sampleChart.refreshChart({
        animalsByGroup: percentileList,
        currentIndex,
        urlImage: currentAnalysis.imageData.reduced.url,
      });

      return;
    }

    const props: SampleD3Props = {
      container: refChart.current,
      height: image.height,
      width: image.width,
      urlImage: currentAnalysis.imageData.reduced.url,
      animalsByGroup: percentileList,
      phaseType: analysisPhaseType,
      translateX: - (image.width / 3),
      translateY: - (image.height / 3),
      enableScroll: false,
    };
    sampleChart = new SampleD3(props);
  }, [refChart.current, currentIndex, percentileList, image.height, image.width, currentAnalysis._id, currentAnalysis?.imageData?.reduced?.url, analysisPhaseType, larvaeModifiedList?.length, image]);

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.center}>
          <Spin />
        </div>

        <div className={styles.containerImage} id='sample'>
          <div ref={refChart} />
          <PreviousNextButtons />
        </div>
      </div>
    );
  }

  function renderButtonFullScreen () {
    return (
      <IconButton
        id='fullscreen_button'
        theme={theme}
        onClick={() => dispatch(sampleSlice.setShowModal(true))}
        iconName='fullscreen'
        size='small'
        tooltipText={t('analysis.fullscreen')}
      />
    );
  }

  function renderOption () {
    if (showOptions) {
      return (
        <div className={styles.actions}>
          {renderButtonFullScreen()}
        </div>
      );
    }

    return null;
  }

  return (
    <div className={cx(containerClassName, 'sample')}>
      {renderOption()}

      <div className={styles.containerImage} id='sample'>
        <div ref={refChart} />
        <PreviousNextButtons />
      </div>

      <SampleFullScreen image={image}/>
    </div>
  );
}
